import { createTheme, Divider, MantineProvider, Stack, Switch } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { TaskStatus } from '../../../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { FilterPanelGroup } from '../../../../../components/filters/filter-panel-group';
import { useRelatedTasksTabStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setRelatedTasksSelectedThroughput,
  setRelatedTasksSelectedTypes,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { secondaryBase } from '../../../../../styles/design-tokens';
import type { FilterGroup } from '../../../initiative-list/filters/filters.type';
import { useTaskTypesOptions } from './filters.hooks';

const theme = createTheme({
  cursorType: 'pointer',
});

export const FiltersTasks = () => {
  const i18n = useTranslation();
  const selectedThroughput = useRelatedTasksTabStore((state) => state.selectedThroughput);
  const selectedTypes = useRelatedTasksTabStore((state) => state.selectedTypes);

  const [isCollapsed, { toggle }] = useDisclosure(false);

  const taskTypesOptions = useTaskTypesOptions();

  const taskTypeFilterGroup = {
    id: 'status',
    name: i18n.t('strategy.filters.types'),
    icon: 'filter_tilt_shift',
    options: taskTypesOptions,
    selectedValues: selectedTypes,
    onSelectionChange: (values: string[]) => setRelatedTasksSelectedTypes(values as TaskStatus[]),
    showAllLabel: i18n.t('strategy.filters.all_types'),
  };

  const handleAllChange = (group: FilterGroup, isChecked: boolean): void => {
    group.onSelectionChange(isChecked ? group.options.map((opt) => opt.value) : []);
  };

  const handleIndividualChange = (group: FilterGroup, value: string, isChecked: boolean): void => {
    const newValues = isChecked ? [...group.selectedValues, value] : group.selectedValues.filter((v) => v !== value);

    group.onSelectionChange(newValues);
  };

  return (
    <Stack gap="md">
      <MantineProvider theme={theme}>
        <Switch
          label={i18n.t('strategy.filters.completed_only')}
          color={secondaryBase}
          size="xs"
          checked={selectedThroughput === 'completed'}
          onChange={() => setRelatedTasksSelectedThroughput(selectedThroughput === 'completed' ? 'total' : 'completed')}
        />
      </MantineProvider>
      <Divider />
      <FilterPanelGroup
        group={taskTypeFilterGroup}
        toggleCollapsed={toggle}
        isCollapsed={isCollapsed}
        isAllSelected={taskTypesOptions.every((option) => selectedTypes.includes(option.value as TaskStatus))}
        handleAllChange={handleAllChange}
        handleIndividualChange={handleIndividualChange}
        hasDivider
      />
    </Stack>
  );
};
